/**
 * Getting a Turnstile token in the browser. The widget is invisible: it runs Cloudflare's checks in
 * the background and returns a token, which the Worker verifies before starting a session or
 * sending an email (AUT-010). The script is loaded once, on first use.
 *
 * Without a configured site key this uses Cloudflare's published test key that always passes, so
 * development needs no Cloudflare account.
 */

interface TurnstileApi {
  render(
    element: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      "error-callback": () => void;
      size: "invisible" | "flexible";
    },
  ): string;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const TEST_SITE_KEY = "1x00000000000000000000BB";

const SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let loading: Promise<TurnstileApi> | null = null;

function load(): Promise<TurnstileApi> {
  loading ??= new Promise((resolve, reject) => {
    const script = document.createElement("script");

    script.src = SCRIPT;
    script.async = true;
    script.addEventListener("load", () =>
      window.turnstile ? resolve(window.turnstile) : reject(new Error("turnstile missing")),
    );
    script.addEventListener("error", () => reject(new Error("turnstile failed to load")));
    document.head.append(script);
  });

  return loading;
}

export async function turnstileToken(): Promise<string> {
  const api = await load();
  const host = document.createElement("div");

  document.body.append(host);

  return new Promise((resolve, reject) => {
    const widget = api.render(host, {
      sitekey: import.meta.env["VITE_TURNSTILE_SITE_KEY"] ?? TEST_SITE_KEY,
      size: "invisible",
      callback: (token) => {
        api.remove(widget);
        host.remove();
        resolve(token);
      },
      "error-callback": () => {
        api.remove(widget);
        host.remove();
        reject(new Error("turnstile check failed"));
      },
    });
  });
}
